import React, { useMemo, useState } from "react";
import DragEmailEditor from "./DragEmailEditor";
import HtmlEmailEditor from "./HtmlEmailEditor";

const categories = [
  "All",
  "Custom",
  "Lifecycle",
  "SaaS",
  "Leap Year",
  "Ramadan",
  "Color of The Year",
  "Agency",
  "Inspiration",
  "The Big Game",
  "Global Observances",
  "Dark Mode",
  "Photography",
  "Wedding"
];

// Built-in starter templates for the code-based editor types only.
const templateData = {
  ai: [
    { id: "ai-empty", title: "Blank AI Email", category: "Custom", theme: "aiBlank" },
    { id: "ai-welcome", title: "Welcome Email", category: "Lifecycle", theme: "welcome" },
    { id: "ai-launch", title: "Launch Announcement", category: "SaaS", theme: "launch" },
    { id: "ai-offer", title: "Special Offer", category: "Agency", theme: "offer" },
    { id: "ai-news", title: "Weekly Newsletter", category: "Inspiration", theme: "news" },
    { id: "ai-reengage", title: "Re-engagement Campaign", category: "Lifecycle", theme: "reengage" }
  ],
  html: [
    { id: "html-empty", title: "Empty", category: "Custom", theme: "htmlEmpty" },
    { id: "html-basic", title: "Simple HTML Newsletter", category: "Custom", theme: "htmlBasic" },
    { id: "html-product", title: "Product HTML Template", category: "SaaS", theme: "htmlProduct" }
  ]
};

// merge-tag helper (avoids writing raw double-brace tokens)
const mt = (n) => "{" + "{" + n + "}" + "}";

// dangerouslySetInnerHTML payload helper (avoids literal double-brace in JSX)
const H = (h) => ({ __html: h });

// build a self-contained document for previewing a template's HTML inside an
// isolated iframe (styles can't leak into the app, design shows complete)
const frameDoc = (h) =>
  '<!doctype html><html><head><meta charset="utf-8"/>' +
  '<meta name="viewport" content="width=600, initial-scale=1"/>' +
  '<style>html,body{margin:0;padding:16px;background:#fff;font-family:Arial,Helvetica,sans-serif;}img{max-width:100%;height:auto;}</style>' +
  '</head><body>' + (h || "") + '</body></html>';

const MY_TPL_KEY = "sx_my_templates";

function loadMyTemplates() {
  try {
    const raw = localStorage.getItem(MY_TPL_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
}

function persistMyTemplates(list) {
  try {
    localStorage.setItem(MY_TPL_KEY, JSON.stringify(list));
  } catch (e) {
    /* ignore */
  }
}

function starterHtml(title) {
  return (
    "<h2>" + (title || "My Template") + "</h2>\n" +
    "<p>Hello " + mt("first_name") + ",</p>\n" +
    "<p>This is an editable newsletter template. You can update this content in the next step.</p>\n" +
    '<a href="#">Call to Action</a>'
  );
}

export default function DesignStep({ form, updateForm }) {
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");

  // saved (custom) templates
  const [myTemplates, setMyTemplates] = useState(loadMyTemplates);

  // save-as-template modal (code editor types)
  const [saveOpen, setSaveOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplCategory, setTplCategory] = useState("Custom");
  const [tplHtml, setTplHtml] = useState("");

  // visual drag & drop editor
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const isDrag = form.editorType === "drag";
  const isHtml = form.editorType === "html";
  // editor types that create/edit their templates in a full editor overlay
  const isEditorType = isDrag || isHtml;
  const builtins = templateData[form.editorType] || [];

  const mine = useMemo(
    () =>
      myTemplates.filter(
        (t) => (t.editorType || "drag") === form.editorType
      ),
    [myTemplates, form.editorType]
  );

  // Drag & drop and HTML editor show ONLY the user's own saved templates.
  const templates = useMemo(
    () => (isEditorType ? mine : [...mine, ...builtins]),
    [isEditorType, mine, builtins]
  );

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const categoryMatch = category === "All" || template.category === category;
      const searchMatch = template.title
        .toLowerCase()
        .includes(search.toLowerCase());
      return categoryMatch && searchMatch;
    });
  }, [templates, category, search]);

  function selectTemplate(template) {
    updateForm("template", template.id);
    if (template.custom && template.html) {
      updateForm("html", template.html);
      return;
    }
    updateForm("html", starterHtml(template.title));
  }

  /* ---------- visual editor open/save ---------- */
  function openEditor(tpl) {
    setEditing(tpl || null);
    setEditorOpen(true);
  }

  function closeEditor() {
    setEditorOpen(false);
    setEditing(null);
  }

  function saveFromEditor(data) {
    let next;
    let activeId;
    if (editing && editing.id) {
      activeId = editing.id;
      next = myTemplates.map((t) =>
        t.id === editing.id
          ? {
              ...t,
              title: data.title,
              category: data.category,
              html: data.html,
              design: data.design
            }
          : t
      );
    } else {
      activeId = "my-" + Date.now();
      const rec = {
        id: activeId,
        title: data.title,
        category: data.category,
        theme: "custom",
        custom: true,
        editorType: form.editorType,
        html: data.html
      };
      if (data.design !== undefined) rec.design = data.design;
      next = [rec, ...myTemplates];
    }
    setMyTemplates(next);
    persistMyTemplates(next);
    setCategory(data.category);
    updateForm("template", activeId);
    updateForm("html", data.html);
    closeEditor();
  }

  /* ---------- code-editor save-as-template modal ---------- */
  function openSaveModal() {
    setTplName(form.name ? form.name + " template" : "");
    setTplCategory("Custom");
    setTplHtml(form.html && form.html.trim() ? form.html : starterHtml(form.name));
    setSaveOpen(true);
  }

  function confirmSave() {
    const name = tplName.trim() || "Untitled template";
    const rec = {
      id: "my-" + Date.now(),
      title: name,
      category: tplCategory || "Custom",
      theme: "custom",
      custom: true,
      editorType: form.editorType,
      html: tplHtml || ""
    };
    const next = [rec, ...myTemplates];
    setMyTemplates(next);
    persistMyTemplates(next);
    setSaveOpen(false);
    setCategory(rec.category);
    updateForm("template", rec.id);
    updateForm("html", rec.html);
  }

  function deleteMyTemplate(id, e) {
    e.stopPropagation();
    const next = myTemplates.filter((t) => t.id !== id);
    setMyTemplates(next);
    persistMyTemplates(next);
  }

  function editMyTemplate(tpl, e) {
    e.stopPropagation();
    openEditor(tpl);
  }

  return (
    <div className="sx-design-layout">
      <aside className="sx-template-sidebar">
        <input placeholder="Search categories..." />
        <div className="sx-category-list">
          {categories.map((item) => (
            <button
              type="button"
              key={item}
              className={category === item ? "active" : ""}
              onClick={() => setCategory(item)}
            >
              {item}
            </button>
          ))}
        </div>
      </aside>

      <section className="sx-template-panel">
        <div className="sx-template-toolbar">
          <div className="sx-template-search">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates..."
            />
          </div>

          {isEditorType ? (
            <button
              type="button"
              className="sx-save-template-btn"
              onClick={() => openEditor(null)}
            >
              {isDrag ? "+ Create with Drag & Drop" : "+ Create with HTML Editor"}
            </button>
          ) : (
            <button
              type="button"
              className="sx-save-template-btn"
              onClick={openSaveModal}
            >
              + Save as Template
            </button>
          )}
        </div>

        <div className="sx-template-grid">
          {isEditorType && (
            <div
              role="button"
              tabIndex={0}
              className="sx-template-card sx-create-card"
              onClick={() => openEditor(null)}
            >
              <div className="sx-template-preview sx-create-preview">
                <div className="sx-create-plus">+</div>
                <strong>{isDrag ? "Drag & Drop Editor" : "HTML Editor"}</strong>
                <p>{isDrag ? "Design a new email visually" : "Write or paste your HTML code"}</p>
                <div className="sx-card-hover">
                  <button
                    type="button"
                    className="sx-card-btn sx-card-btn-primary"
                    onClick={(e) => { e.stopPropagation(); openEditor(null); }}
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          )}

          {filteredTemplates.map((template) => (
            <div
              role="button"
              tabIndex={0}
              key={template.id}
              className={`sx-template-card ${
                form.template === template.id ? "active" : ""
              } ${template.custom ? "is-custom" : ""}`}
              onClick={() => selectTemplate(template)}
            >
              <div className={`sx-template-preview ${template.theme}`}>
                {template.custom && template.html ? (
                  <iframe
                    className="sx-tpl-frame"
                    title={template.title}
                    sandbox="allow-same-origin"
                    scrolling="no"
                    srcDoc={frameDoc(template.html)}
                  />
                ) : template.theme.includes("empty") ||
                  template.theme.includes("Empty") ? (
                  <div className="sx-empty-template">
                    <strong>
                      {template.theme === "htmlEmpty" ? "</>" : "Empty"}
                    </strong>
                    {template.theme === "htmlEmpty" && <p>Paste your HTML</p>}
                  </div>
                ) : (
                  <div className="sx-mock-email">
                    <small>Your Logo</small>
                    <h3>{template.title}</h3>
                    <p>
                      Beautiful campaign layout with image, text, and action
                      sections.
                    </p>
                    <span>Start reading →</span>
                  </div>
                )}

                <div className="sx-card-hover">
                  {template.custom ? (
                    <>
                      <button
                        type="button"
                        className="sx-card-btn sx-card-btn-primary"
                        onClick={(e) => editMyTemplate(template, e)}
                      >
                        Edit template
                      </button>
                      <button
                        type="button"
                        className="sx-card-btn sx-card-btn-dark"
                        onClick={(e) => deleteMyTemplate(template.id, e)}
                      >
                        Delete template
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="sx-card-btn sx-card-btn-primary"
                      onClick={(e) => { e.stopPropagation(); selectTemplate(template); }}
                    >
                      Select Template
                    </button>
                  )}
                </div>
              </div>

              <b>{template.title}</b>
            </div>
          ))}

          {isEditorType && filteredTemplates.length === 0 && (
            <div className="sx-drag-empty-note">
              You have no saved {isDrag ? "drag & drop" : "HTML"} templates yet. Click
              <strong> {isDrag ? " Create with Drag & Drop " : " Create with HTML Editor "} </strong> to make one.
            </div>
          )}
        </div>

        <div className="sx-template-footer">
          <div className="sx-pager">
            <button type="button">←</button>
            <button type="button">→</button>
          </div>
        </div>
      </section>

      {saveOpen && (
        <div className="sx-modal-overlay">
          <div className="sx-small-modal sx-save-tpl-modal">
            <h2>Save as Template</h2>
            <p>Save this design so you can reuse it in future campaigns.</p>
            <label>Template name</label>
            <input
              value={tplName}
              onChange={(e) => setTplName(e.target.value)}
              placeholder="e.g. My Monthly Newsletter"
            />
            <label>Category</label>
            <select
              value={tplCategory}
              onChange={(e) => setTplCategory(e.target.value)}
            >
              {categories
                .filter((c) => c !== "All")
                .map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
            </select>
            <label>Content (HTML)</label>
            <textarea
              className="sx-save-tpl-html"
              value={tplHtml}
              onChange={(e) => setTplHtml(e.target.value)}
              placeholder="Your template content..."
            />
            <div className="sx-modal-actions">
              <button type="button" onClick={() => setSaveOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="sendx-yellow-btn"
                onClick={confirmSave}
              >
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      {editorOpen && isDrag && (
        <DragEmailEditor
          initial={editing}
          onSave={saveFromEditor}
          onClose={closeEditor}
        />
      )}

      {editorOpen && isHtml && (
        <HtmlEmailEditor
          initial={editing}
          onSave={saveFromEditor}
          onClose={closeEditor}
        />
      )}
    </div>
  );
}
